import {
  dataAttribute,
  DataAttributes,
  IColumn,
  RESET_CELLS_PADDING_CLASSNAME,
  Text
} from "@bps/fluent-ui";
import { routes } from "@libs/routing/routes.ts";
import { ClaimAdjustmentCallout } from "@modules/acc/screens/claim-adjustment/components/ClaimAdjustmentCallout.tsx";
import { isClaimAdjustmentAllowed } from "@modules/acc/screens/claim-adjustment/components/utils.ts";
import { ClaimStatusBadge } from "@modules/acc/screens/shared-components/claim-status-badge/ClaimStatusBadge.tsx";
import { Claim } from "@stores/acc/models/Claim.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";
import { Navigate } from "@ui-components/navigation/Navigate.tsx";

import { ClaimNumberCell } from "./ClaimNumberCell.tsx";
import { ClaimsContextualMenu } from "./ClaimsContextualMenu.tsx";
import { ClaimsListInjuryDiagnosisText } from "./ClaimsListInjuryDiagnosisText.tsx";

export const useClaimsListColumns = (): IColumn[] => {
  return [
    {
      name: "Actions",
      className: RESET_CELLS_PADDING_CLASSNAME,
      key: "action",
      minWidth: 50,
      maxWidth: 50,
      onRender: (claim: Claim) => <ClaimsContextualMenu claim={claim} />
    },
    useClaimsListStatusColumn(),
    useClaimsListClaimNumberColumn(),
    useClaimsListPatientColumn(),
    useClaimsListDiagnosisColumn(),
    useClaimsListProviderColumn(),
    {
      name: "Initial consult",
      key: "initialConsultDate",
      minWidth: 80,
      maxWidth: 100,
      onRender: (claim: Claim) =>
        !!claim.initialConsultDate &&
        claim.initialConsultDate.toDayDefaultFormat()
    },
    {
      name: "ACC32",
      key: "claimAdjustments",
      minWidth: 120,
      maxWidth: 120,
      onRender: (claim: Claim) => {
        if (
          claim.claimAdjustments.length > 1 &&
          isClaimAdjustmentAllowed(claim.claimStatus ?? "")
        ) {
          return <ClaimAdjustmentCallout claim={claim} />;
        } else if (claim.claimAdjustments?.length) {
          return (
            <Navigate
              to={routes.claimAdjustment.edit.path({
                id: claim.claimAdjustments[0].id,
                claimId: claim.id
              })}
            >
              {claim.claimAdjustments[0].formattedNumber}
            </Navigate>
          );
        }
        return;
      }
    },
    {
      name: "Insurer",
      key: "insurerName",
      minWidth: 100,
      maxWidth: 200,
      onRender: (claim: Claim) =>
        claim.insurerName && <Text>{claim.insurerName}</Text>
    }
  ];
};

export const useClaimsListStatusColumn = (): IColumn => {
  return {
    name: "Error status",
    key: "claimStatus",
    className: RESET_CELLS_PADDING_CLASSNAME,
    minWidth: 130,
    maxWidth: 130,
    onRender: (claim: Claim) => (
      <ClaimStatusBadge
        styles={{
          root: {
            horizontalAlign: "start",
            styles: { root: { marginLeft: 12 } }
          }
        }}
        claimId={claim.id}
        status={claim.claimStatus}
        isDischarged={claim.isDischarged}
      />
    )
  };
};

export const useClaimsListClaimNumberColumn = (): IColumn => {
  const { routing } = useStores();
  const searchString = routing.queryStringParam("claimNumber") || "";

  return {
    name: "Claim",
    key: "claimNumber",
    minWidth: 80,
    maxWidth: 100,
    onRender: (claim: Claim) => (
      <ClaimNumberCell claim={claim} filter={searchString} />
    )
  };
};

export const useClaimsListPatientColumn = (): IColumn => {
  const patientLabel = usePatientLabel(true);
  return {
    name: patientLabel,
    key: "name",
    minWidth: 100,
    maxWidth: 200,
    onRender: (claim: Claim) =>
      claim.patientId && (
        <Navigate
          {...dataAttribute(DataAttributes.Element, "patient-Name")}
          to={routes.contacts.contact.path({
            id: claim.patientId
          })}
        >
          {claim.patientFullName}
        </Navigate>
      )
  };
};

export const useClaimsListDiagnosisColumn = (): IColumn => {
  return {
    name: "Diagnosis",
    key: "injuryDiagnosis",
    minWidth: 100,
    maxWidth: 150,
    onRender: (claim: Claim) => (
      <Text nowrap>
        {!!claim.currentDiagnoses && claim.currentDiagnoses.length > 0 && (
          <ClaimsListInjuryDiagnosisText diagnosis={claim.currentDiagnoses} />
        )}
      </Text>
    )
  };
};

export const useClaimsListProviderColumn = (): IColumn => {
  return {
    name: "Provider",
    key: "provider",
    minWidth: 150,
    maxWidth: 200,
    onRender: (claim: Claim) =>
      claim.providerFullName && <Text>{claim.providerFullName}</Text>
  };
};
